import Layout from '@components/layout'
import { shopSalePageModuleQuery } from '@data/sanity/queries/page'
import { SanityShopSalePage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext } from '@lib/routes'
import { defaultModuleQueryParameters, getShopSalePage } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ShopSalePageProps = BasePageProps & {
  page: SanityShopSalePage
  site: SanitySiteFragment
}

const ShopSalePage = ({ draftMode, locale, page, site }: ShopSalePageProps) => {
  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && (
        <Modules
          modules={page.modules}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopSalePage"
          quickFilterBasePath="/shop/sale"
          quickFilterAllPath="/shop/sale"
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={shopSalePageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopSalePage"
          quickFilterBasePath="/shop/sale"
          quickFilterAllPath="/shop/sale"
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<ShopSalePageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const shopSalePage = await getShopSalePage(sanityClient, context.locale)

  if (!shopSalePage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(shopSalePage.page),
      site: getNonNullValues(shopSalePage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default ShopSalePage
